import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { find, map } from 'lodash'

import { Select } from '@/components/ui/select'
import { useDeliverableSetList } from '@/features/deliverable-set/hooks'
import { DEFAULT_GET_ALL_LIMIT, SortDirection } from '@/utils/constants'

import { DeliverableSetStatus } from '../../../schema'
import { useFileUploadStore } from '../store'
import { DeliverableSetOption } from '../types'

export const DeliverableSetSelect: React.FC<{
  mode?: 'editable' | 'readonly'
  className?: string
  disabled?: boolean
}> = ({ mode = 'editable', className = '', disabled = false }) => {
  const { t } = useTranslation()
  const {
    selectedDeliverableSet,
    setSelectedDeliverableSet,
    contextDeliverableSetId,
  } = useFileUploadStore()

  const {
    data: { data: deliverableSetsData = [] } = {},
    isLoading,
    isError,
  } = useDeliverableSetList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'deliverable_set_name', direction: SortDirection.ASC },
  })

  const options: DeliverableSetOption[] = useMemo(() => {
    return map(
      deliverableSetsData.filter(
        (ds) =>
          ds.status !== DeliverableSetStatus.APPROVED &&
          ds.status !== DeliverableSetStatus.ARCHIVED
      ),
      (ds) => ({
        id: ds.id,
        name: ds.deliverable_set_name || '',
        projectName: ds.project_name,
        projectId: ds.project_id,
      })
    )
  }, [deliverableSetsData])

  React.useEffect(() => {
    if (
      contextDeliverableSetId &&
      options.length > 0 &&
      !selectedDeliverableSet
    ) {
      const contextDs = find(options, { id: contextDeliverableSetId })
      if (contextDs) {
        setSelectedDeliverableSet(contextDs)
      }
    }
  }, [
    contextDeliverableSetId,
    options,
    selectedDeliverableSet,
    setSelectedDeliverableSet,
  ])

  const handleChange = (value: string) => {
    const selected = find(options, { id: value })
    setSelectedDeliverableSet(selected || null)
  }

  const labelClass = 'block text-sm font-medium text-secondary-700'

  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        <label className={labelClass}>
          {t('upload.deliverableSetSelect.label')}
        </label>
        <div className="text-sm text-tertiary-600">
          {t('upload.deliverableSetSelect.loading')}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={`space-y-2 ${className}`}>
        <label className={labelClass}>
          {t('upload.deliverableSetSelect.label')}
        </label>
        <div className="text-sm text-destructive-600">
          {t('upload.deliverableSetSelect.error')}
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <label className={labelClass}>
        {t('upload.deliverableSetSelect.label')}
      </label>
      <Select
        value={selectedDeliverableSet?.id || ''}
        onChange={handleChange}
        placeholder={t('upload.deliverableSetSelect.placeholder')}
        options={options.map((opt) => ({
          label: opt.name,
          value: opt.id,
        }))}
        disabled={mode === 'readonly' || disabled}
        searchable
        className="[&_.radix-select-trigger]:w-[320px]"
        valueClassName="font-medium text-primary-900"
      />
    </div>
  )
}
